import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { BoardRuntime, type BoardElement, type BoardScheme, type BoardViewport } from '@/scenes/Room/board/boardEngine';

/**
 * Plays a lesson's whiteboards on the recorded timing, without a session: recorder `scripts/plan_boards.py --out`
 * writes the JSON into public/dev-boards/. ?board=all&instant=1 puts every board up at once (headless screenshots).
 */
export const Route = createFileRoute('/dev/board-preview')({
  validateSearch: (search: Record<string, unknown>) => ({
    file: typeof search.file === 'string' ? search.file : 'nn',
    board: search.board === 'all' ? ('all' as const) : Number(search.board ?? 0) || 0,
    instant: search.instant === 1 || search.instant === '1' || search.instant === true,
    scheme: (search.scheme === 'dark' ? 'dark' : 'light') as BoardScheme,
  }),
  component: BoardPreview,
});

interface PreviewStep {
  kind: string;
  say: string;
  sentences: string[];
  sentence_starts: number[];
  duration: number;
  card: string;
  image_url: string;
}
interface PreviewBoard {
  title: string;
  viewport: BoardViewport;
  equal_scale: boolean;
  elements: (BoardElement & { step: number; sentence: number })[];
}
interface Preview {
  title: string;
  steps: PreviewStep[];
  boards: PreviewBoard[];
}

declare global {
  interface Window {
    __boardPreview?: { done: boolean; boards: number };
  }
}

// Seconds into the step where each sentence starts: the recorded alignment, or ~15 characters a second without audio.
function sentenceTimes(step: PreviewStep) {
  if (step.sentence_starts.length === step.sentences.length) return step.sentence_starts;
  let t = 0;
  return step.sentences.map((s) => {
    const at = t;
    t += s.length / 15;
    return at;
  });
}

function BoardCanvas({ board, scheme, instant, playKey, onCaption }: {
  board: PreviewBoard;
  scheme: BoardScheme;
  instant: boolean;
  playKey: number;
  onCaption?: (text: string) => void;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [runtime, setRuntime] = useState<BoardRuntime | null>(null);

  // Created in an effect, not a memo: StrictMode's second mount would otherwise get a disposed board.
  useEffect(() => {
    const rt = new BoardRuntime(`preview-${playKey}`, board);
    rt.sound = false;
    setRuntime(rt);
    // How far the hand got, with the time each element finished: a headless browser reads this to time the hand.
    const t0 = performance.now();
    const finished: number[] = [];
    const off = rt.onChange(() => {
      finished.push(Math.round(performance.now() - t0));
      (window as unknown as { __boardDrawn: number[] }).__boardDrawn = finished;
    });
    return () => {
      off();
      rt.dispose();
    };
  }, [board, playKey]);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || !runtime) return;
    runtime.setScheme(scheme);
    runtime.attach(canvas);
    return () => runtime.detach(canvas);
  }, [runtime, scheme]);

  useEffect(() => {
    if (!runtime) return;
    if (instant) {
      runtime.enqueue(board.elements, { instant: true });
      return;
    }
    if (!onCaption) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    void (async () => {
      const steps = (window as unknown as { __previewSteps: PreviewStep[] }).__previewSteps;
      const first = board.elements[0]?.step ?? 0;
      const last = board.elements[board.elements.length - 1]?.step ?? first;
      let offset = 0;
      for (let s = first; s <= last; s++) {
        const step = steps[s];
        const times = sentenceTimes(step);
        times.forEach((t, k) => {
          timers.push(setTimeout(() => {
            onCaption(step.kind === 'say' ? step.sentences[k] : `[${step.kind}] ${step.card || ''} ${step.sentences[k] || ''}`);
            runtime.enqueue(board.elements.filter((el) => el.step === s && el.sentence === k));
          }, (offset + t) * 1000));
        });
        offset += step.duration || (times[times.length - 1] ?? 0) + 4;
      }
      timers.push(setTimeout(() => onCaption(''), offset * 1000));
    })();
    return () => timers.forEach(clearTimeout);
  }, [runtime, board, instant, onCaption]);

  return <canvas ref={ref} className="block w-full aspect-[16/10] rounded-xl ring-1 ring-black/10" />;
}

function BoardPreview() {
  const { file, board, instant, scheme } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [data, setData] = useState<Preview | null>(null);
  const [error, setError] = useState('');
  const [caption, setCaption] = useState('');
  const [playKey, setPlayKey] = useState(0);

  useEffect(() => {
    fetch(`/dev-boards/${file}.json`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`${r.status} for /dev-boards/${file}.json`))))
      .then((d: Preview) => {
        (window as unknown as { __previewSteps: PreviewStep[] }).__previewSteps = d.steps;
        setData(d);
      })
      .catch((e: Error) => setError(e.message));
  }, [file]);

  useEffect(() => {
    if (!data || !instant) return;
    // Instant boards finish after the font loads; give the hand a moment, then tell a headless browser it's done.
    const t = setTimeout(() => { window.__boardPreview = { done: true, boards: data.boards.length }; }, 1500);
    return () => clearTimeout(t);
  }, [data, instant]);

  if (error) return <div className="p-4 text-sm text-red-600">{error}</div>;
  if (!data) return <div className="p-4 text-sm text-neutral-500">Loading…</div>;

  const dark = scheme === 'dark';
  const pick = (next: Partial<{ board: number | 'all'; instant: boolean; scheme: BoardScheme }>) =>
    navigate({ search: (prev) => ({ ...prev, ...next }) });

  return (
    <div className={`min-h-screen ${dark ? 'bg-neutral-950 text-neutral-100' : 'bg-neutral-100 text-neutral-900'}`}>
      <div className="mx-auto max-w-[1180px] p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <h1 className="mr-2 font-semibold">{data.title}</h1>
          {data.boards.map((b, i) => (
            <button
              key={i}
              onClick={() => pick({ board: i })}
              className={`rounded-full px-3 py-1 ${board === i ? 'bg-neutral-900 text-white' : 'bg-white/70 text-neutral-800'}`}
            >
              {i + 1}. {b.title}
            </button>
          ))}
          <button onClick={() => pick({ board: 'all' })} className="rounded-full bg-white/70 px-3 py-1 text-neutral-800">All</button>
          <button onClick={() => pick({ instant: !instant })} className="rounded-full bg-white/70 px-3 py-1 text-neutral-800">
            {instant ? 'Play on timing' : 'Draw at once'}
          </button>
          <button onClick={() => pick({ scheme: dark ? 'light' : 'dark' })} className="rounded-full bg-white/70 px-3 py-1 text-neutral-800">
            {dark ? 'Light' : 'Dark'}
          </button>
          {!instant && board !== 'all' && (
            <button onClick={() => setPlayKey((k) => k + 1)} className="rounded-full bg-white/70 px-3 py-1 text-neutral-800">Replay</button>
          )}
        </div>

        {board === 'all' ? (
          <div className="grid grid-cols-2 gap-3">
            {data.boards.map((b, i) => (
              <div key={i}>
                <p className="mb-1 text-xs opacity-70">{i + 1}. {b.title} · {b.elements.length} elements</p>
                <BoardCanvas board={b} scheme={scheme} instant playKey={i} />
              </div>
            ))}
          </div>
        ) : data.boards[board] ? (
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_280px]">
            <div className="space-y-2">
              <BoardCanvas board={data.boards[board]} scheme={scheme} instant={instant} playKey={playKey * 100 + board} onCaption={setCaption} />
              <p className="min-h-[3rem] text-[15px] leading-6">{caption}</p>
            </div>
            <ol className="max-h-[80vh] space-y-0.5 overflow-auto text-xs">
              {data.boards[board].elements.map((el, i) => (
                <li key={i} className="flex gap-2">
                  <span className="w-10 shrink-0 tabular-nums opacity-60">{el.step}.{el.sentence}</span>
                  <span className="w-20 shrink-0 opacity-80">{el.type}</span>
                  <span>{el.note}</span>
                </li>
              ))}
            </ol>
          </div>
        ) : (
          <p className="text-sm opacity-70">This lesson has no boards.</p>
        )}
      </div>
    </div>
  );
}
